import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import ModalScrim from "./ModalScrim";
import RowActionBar from "./RowActionBar";
import GovernanceApprovalModal from "./GovernanceApprovalModal";
import { CLIENT_PAGE_SIZES, paginateRows } from "../lib/clientPagination";
import {
  approvalRequiredFor,
  displayCell,
  emptyRow,
  normalizeCrudRows,
  rowActionsFor,
  rowStableId,
  validateCrudForm,
} from "../lib/adminCrudConfig";

export default function AdminCrudPanel({
  panelConfig,
  rows: seedRows,
  onRowsChange,
  onPersist,
  dependencyOptions = {},
  contextOptions = {},
  testId = "admin-crud",
}) {
  const [rows, setRows] = useState(seedRows || []);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [review, setReview] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [accessTarget, setAccessTarget] = useState(null);
  const [accessForm, setAccessForm] = useState({});
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveProgramId, setMoveProgramId] = useState("");
  const [governanceApproval, setGovernanceApproval] = useState(null);
  const [msg, setMsg] = useState("");
  const defaultPageSize = panelConfig.pageSize ?? 10;
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setRows(normalizeCrudRows(seedRows || [], panelConfig));
  }, [seedRows, panelConfig]);

  useEffect(() => {
    setPage(1);
  }, [seedRows, pageSize, panelConfig.rowKey]);

  const rowKey = panelConfig.rowKey || "id";
  const actions = rowActionsFor(panelConfig);
  const entityLabel = panelConfig.entityLabel || "Record";
  const columns = panelConfig.columns || [];
  const fields = panelConfig.fields || [];
  const accessFields = panelConfig.accessFields || [];
  const dependencies = panelConfig.dependencies || [];
  const programOptions = contextOptions.programs || [];

  const pagination = useMemo(
    () => paginateRows(rows, page, pageSize),
    [rows, page, pageSize],
  );

  const sync = (next) => {
    const normalized = normalizeCrudRows(next, panelConfig);
    setRows(normalized);
    if (onRowsChange) onRowsChange(normalized);
  };

  const openCreate = () => {
    setForm(emptyRow(panelConfig));
    setModal({ mode: "create" });
    setMsg("");
  };

  const openView = (row) => {
    setForm({ ...row });
    setModal({ mode: "view", row });
  };

  const openEdit = (row) => {
    setForm({ ...row });
    setModal({ mode: "edit", row });
    setMsg("");
  };

  const applyChange = async (payload, meta) => {
    const { mode, row, rationale } = meta;
    if (mode === "create") {
      const keyVal = payload[rowKey];
      if (rows.some((r) => String(r[rowKey]) === String(keyVal))) {
        setMsg(`A row with this ${rowKey} already exists`);
        return false;
      }
      const next = [...rows, { ...payload, _approval: rationale || null }];
      sync(next);
      if (onPersist) await onPersist("create", payload, rationale);
      setMsg(`Added ${entityLabel.toLowerCase()} ${keyVal}`);
    } else if (mode === "edit") {
      sync(rows.map((r) => (String(r[rowKey]) === String(row[rowKey]) ? { ...payload, _approval: rationale || null } : r)));
      if (onPersist) await onPersist("edit", payload, rationale);
      setMsg(`Updated ${entityLabel.toLowerCase()} ${payload[rowKey]}`);
    } else if (mode === "delete") {
      sync(rows.filter((r) => String(r[rowKey]) !== String(row[rowKey])));
      if (onPersist) await onPersist("delete", row, rationale);
      setMsg(`Deleted ${entityLabel.toLowerCase()} ${row[rowKey]}`);
    } else if (mode === "access") {
      sync(rows.map((r) => (String(r[rowKey]) === String(row[rowKey]) ? { ...r, ...payload, _approval: rationale || null } : r)));
      if (onPersist) await onPersist("access", { ...row, ...payload }, rationale);
      setMsg(`Updated access for ${payload.email || row[rowKey]}`);
    } else if (mode === "move") {
      sync(rows.map((r) => (String(r[rowKey]) === String(row[rowKey]) ? { ...r, program_id: payload.program_id, _approval: rationale || null } : r)));
      if (onPersist) await onPersist("move", { ...row, ...payload }, rationale);
      setMsg(`Moved ${entityLabel.toLowerCase()} ${row[rowKey]} to ${payload.program_id}`);
    }
    return true;
  };

  const openAccess = (row) => {
    const next = {};
    accessFields.forEach((f) => {
      next[f.key] = row[f.key] ?? row.decisions?.[f.key.replace("auth_", "")] ?? f.default ?? "NONE";
    });
    setAccessForm(next);
    setAccessTarget(row);
    setMsg("");
  };

  const submitAccess = (e) => {
    e.preventDefault();
    if (!accessTarget) return;
    const payload = { ...accessTarget, ...accessForm, email: accessTarget.email };
    if (approvalRequiredFor(panelConfig, "access")) {
      setGovernanceApproval({
        mode: "access",
        row: accessTarget,
        payload,
        title: `Approve access changes · ${accessTarget.email}`,
        summary: accessFields
          .filter((f) => accessForm[f.key])
          .map((f) => ({ label: f.label, value: String(accessForm[f.key]) })),
      });
      setAccessTarget(null);
      return;
    }
    applyChange(payload, { mode: "access", row: accessTarget }).then((ok) => {
      if (ok) setAccessTarget(null);
    });
  };

  const openMove = (row) => {
    setMoveTarget(row);
    setMoveProgramId(row.program_id || programOptions[0]?.id || "");
    setMsg("");
  };

  const submitMove = (e) => {
    e.preventDefault();
    if (!moveTarget || !moveProgramId) {
      setMsg("Select a destination program.");
      return;
    }
    const payload = { ...moveTarget, program_id: moveProgramId };
    if (approvalRequiredFor(panelConfig, "move")) {
      setGovernanceApproval({
        mode: "move",
        row: moveTarget,
        payload,
        title: `Approve move · ${moveTarget.id}`,
        summary: [
          { label: "Application", value: moveTarget.name || moveTarget.id },
          { label: "Destination program", value: moveProgramId },
        ],
      });
      setMoveTarget(null);
      return;
    }
    applyChange(payload, { mode: "move", row: moveTarget }).then((ok) => {
      if (ok) setMoveTarget(null);
    });
  };

  const openReview = (row) => {
    setForm({ ...row });
    setModal({ mode: "view", row, reviewMode: true });
  };

  const confirmGovernanceApproval = async (rationale) => {
    if (!governanceApproval) return;
    const { mode, row, payload } = governanceApproval;
    const ok = await applyChange(payload, { mode, row, rationale });
    if (ok) setGovernanceApproval(null);
  };

  const submitForm = (e) => {
    e.preventDefault();
    const trimmed = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v]),
    );
    const validationErrors = validateCrudForm(trimmed, panelConfig);
    for (const dep of dependencies) {
      if (!dep.required) continue;
      const val = trimmed[dep.key];
      if (val == null || String(val).trim() === "") {
        validationErrors.push(`${dep.label} is required (dependency)`);
      }
    }
    if (validationErrors.length) {
      setMsg(validationErrors[0]);
      return;
    }
    if (approvalRequiredFor(panelConfig, modal.mode)) {
      setReview({ mode: modal.mode, payload: trimmed, row: modal.row });
      setModal(null);
      return;
    }
    applyChange(trimmed, { mode: modal.mode, row: modal.row }).then((ok) => {
      if (ok) setModal(null);
    });
  };

  const confirmReview = async (rationale) => {
    if (!review) return;
    const ok = await applyChange(review.payload, {
      mode: review.mode,
      row: review.row,
      rationale,
    });
    if (ok) {
      setReview(null);
      setConfirmDelete(null);
    }
  };

  const requestDelete = (row) => {
    if (approvalRequiredFor(panelConfig, "delete")) {
      setConfirmDelete(row);
    } else {
      setConfirmDelete(row);
    }
  };

  const finalizeDelete = (rationale) => {
    if (!confirmDelete) return;
    if (approvalRequiredFor(panelConfig, "delete") && !(rationale || "").trim()) {
      setMsg("Approval rationale is required to delete.");
      return;
    }
    applyChange(confirmDelete, { mode: "delete", row: confirmDelete, rationale }).then((ok) => {
      if (ok) setConfirmDelete(null);
    });
  };

  return (
    <section className="admin-section admin-crud-section" data-testid={testId}>
      <div className="admin-crud-header">
        <div>
          <p className="kicker">{panelConfig.kicker}</p>
          {panelConfig.title && <h2 className="admin-crud-title">{panelConfig.title}</h2>}
          {panelConfig.detail && <p className="muted small-note">{panelConfig.detail}</p>}
          <p className="muted small-note admin-crud-hint">
            Each row shows <strong>Actions</strong> on the right — View, Review, Access, Move, Modify and Delete as applicable.
            Governed changes may require review and approval rationale.
          </p>
        </div>
        {actions.add && (
          <button
            type="button"
            className="primary-button admin-crud-add"
            data-testid={`${testId}-add`}
            onClick={openCreate}
          >
            <Plus size={16} /> Add new {entityLabel}
          </button>
        )}
      </div>

      {msg && <p className="admin-note" data-testid={`${testId}-msg`}>{msg}</p>}

      {rows.length === 0 ? (
        <div className="admin-crud-empty" data-testid={`${testId}-empty`}>
          <p className="muted">No {entityLabel.toLowerCase()} records yet.</p>
          {actions.add && (
            <button type="button" className="primary-button" onClick={openCreate}>
              <Plus size={16} /> Add new {entityLabel}
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="admin-crud-toolbar" data-testid={`${testId}-toolbar`}>
            <span className="muted small-note" data-testid={`${testId}-count`}>
              {pagination.total} record{pagination.total === 1 ? "" : "s"}
              {pagination.total > 0 && pageSize !== "all" && (
                <> · showing {pagination.showingFrom}–{pagination.showingTo}</>
              )}
            </span>
            <label className="admin-crud-page-size">
              <span>Rows per page</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  const next = e.target.value === "all" ? "all" : Number(e.target.value);
                  setPageSize(next);
                }}
                data-testid={`${testId}-page-size`}
              >
                {CLIENT_PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
                <option value="all">All</option>
              </select>
            </label>
          </div>
          <div className="admin-crud-table-wrap">
            <table className="admin-crud-table" data-testid={`${testId}-table`}>
              <colgroup>
                {columns.map((col) => (
                  <col key={col.key} style={col.width ? { width: col.width } : undefined} />
                ))}
                <col className="admin-crud-actions-col" style={{ width: actions.move || actions.access ? "360px" : "280px" }} />
              </colgroup>
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col.key} scope="col">{col.label}</th>
                  ))}
                  <th className="admin-crud-actions-col" scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagination.pageRows.map((row, rowIndex) => {
                  const stableId = rowStableId(row, rowKey, pagination.start + rowIndex);
                  return (
                    <tr key={stableId} className="admin-crud-row" data-testid={`${testId}-row-${stableId}`}>
                      {columns.map((col) => (
                        <td key={col.key} title={displayCell(row, col)}>{displayCell(row, col)}</td>
                      ))}
                      <td className="admin-crud-actions-cell">
                        <RowActionBar
                          testId={testId}
                          rowId={stableId}
                          view={actions.view}
                          modify={actions.modify}
                          delete={actions.delete}
                          review={actions.review}
                          approve={actions.approve}
                          access={actions.access}
                          move={actions.move && programOptions.length > 0}
                          onView={() => openView(row)}
                          onModify={() => openEdit(row)}
                          onDelete={() => requestDelete(row)}
                          onReview={() => openReview(row)}
                          onAccess={() => openAccess(row)}
                          onMove={() => openMove(row)}
                          readOnly={actions.view && !actions.modify && !actions.delete && !actions.access && !actions.move && !actions.review}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pageSize !== "all" && pagination.total > 0 && (
            <div className="admin-crud-pagination" data-testid={`${testId}-pagination`}>
              <button
                type="button"
                className="secondary-button small-btn"
                disabled={pagination.safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid={`${testId}-page-prev`}
              >
                <ChevronLeft size={14} /> Previous
              </button>
              <span className="admin-crud-page-label" data-testid={`${testId}-page-label`}>
                Page {pagination.safePage} of {pagination.totalPages}
              </span>
              <button
                type="button"
                className="secondary-button small-btn"
                disabled={pagination.safePage >= pagination.totalPages}
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                data-testid={`${testId}-page-next`}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}

      {modal && (
        <AdminCrudModal
          mode={modal.mode}
          entityLabel={entityLabel}
          fields={fields}
          dependencies={dependencies}
          dependencyOptions={dependencyOptions}
          form={form}
          setForm={setForm}
          rowKey={rowKey}
          needsApproval={approvalRequiredFor(panelConfig, modal.mode)}
          onClose={() => setModal(null)}
          onSubmit={submitForm}
          error={msg && modal.mode !== "view" ? msg : ""}
          testId={testId}
        />
      )}

      {review && (
        <ReviewApprovalModal
          mode={review.mode}
          entityLabel={entityLabel}
          payload={review.payload}
          fields={fields}
          onClose={() => setReview(null)}
          onConfirm={confirmReview}
          testId={testId}
        />
      )}

      {confirmDelete && (
        <DeleteApprovalModal
          entityLabel={entityLabel}
          row={confirmDelete}
          rowKey={rowKey}
          needsApproval={approvalRequiredFor(panelConfig, "delete")}
          onClose={() => setConfirmDelete(null)}
          onConfirm={finalizeDelete}
          testId={testId}
        />
      )}

      {accessTarget && (
        <ModalScrim testId={`${testId}-access-modal`}>
          <form className="modal wide" onSubmit={submitAccess}>
            <button type="button" className="modal-close" onClick={() => setAccessTarget(null)}><X size={18} /></button>
            <p className="kicker">ACCESS & DECISION AUTHORITY</p>
            <h2>{accessTarget.name || accessTarget.email}</h2>
            <p className="muted small-note">Session role must be a decision authority. Changes require review and approval.</p>
            {accessFields.map((field) => (
              <label key={field.key}>
                {field.label}
                <select
                  value={accessForm[field.key] ?? field.default ?? "NONE"}
                  onChange={(e) => setAccessForm({ ...accessForm, [field.key]: e.target.value })}
                  data-testid={`${testId}-access-${field.key}`}
                >
                  {["NONE", "VIEW", "RECOMMEND", "APPROVE"].map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </label>
            ))}
            <div className="login-actions">
              <button type="button" className="secondary-button" onClick={() => setAccessTarget(null)}>Cancel</button>
              <button type="submit" className="primary-button" data-testid={`${testId}-access-submit`}>
                {approvalRequiredFor(panelConfig, "access") ? "Continue to review" : "Save access"}
              </button>
            </div>
          </form>
        </ModalScrim>
      )}

      {moveTarget && (
        <ModalScrim testId={`${testId}-move-modal`}>
          <form className="modal wide" onSubmit={submitMove}>
            <button type="button" className="modal-close" onClick={() => setMoveTarget(null)}><X size={18} /></button>
            <p className="kicker">MOVE APPLICATION</p>
            <h2>{moveTarget.name || moveTarget.id}</h2>
            <p className="muted small-note">Associate this application with another program. Requires approval rationale.</p>
            <label>
              Destination program
              <select
                required
                value={moveProgramId}
                onChange={(e) => setMoveProgramId(e.target.value)}
                data-testid={`${testId}-move-program`}
              >
                {programOptions.map((p) => (
                  <option key={p.id} value={p.id}>{p.name || p.id} ({p.id})</option>
                ))}
              </select>
            </label>
            <div className="login-actions">
              <button type="button" className="secondary-button" onClick={() => setMoveTarget(null)}>Cancel</button>
              <button type="submit" className="primary-button" data-testid={`${testId}-move-submit`}>
                {approvalRequiredFor(panelConfig, "move") ? "Continue to review" : "Move application"}
              </button>
            </div>
          </form>
        </ModalScrim>
      )}

      {governanceApproval && (
        <GovernanceApprovalModal
          title={governanceApproval.title}
          kicker={`REVIEW & APPROVAL · ${entityLabel.toUpperCase()}`}
          detail="Review the proposed governance change before it takes effect."
          summary={governanceApproval.summary}
          onClose={() => setGovernanceApproval(null)}
          onConfirm={confirmGovernanceApproval}
          testId={`${testId}-governance-approval`}
        />
      )}
    </section>
  );
}

function AdminCrudModal({
  mode,
  entityLabel,
  fields,
  dependencies,
  dependencyOptions,
  form,
  setForm,
  rowKey,
  needsApproval,
  onClose,
  onSubmit,
  error,
  testId,
}) {
  const readOnly = mode === "view";
  const title = mode === "create" ? `Add new ${entityLabel}` : mode === "edit" ? `Modify ${entityLabel}` : `View ${entityLabel}`;

  return (
    <ModalScrim testId={`${testId}-modal`}>
      <form className="modal wide" onSubmit={readOnly ? (e) => e.preventDefault() : onSubmit}>
        <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        <p className="kicker">{mode.toUpperCase()} · {entityLabel.toUpperCase()}</p>
        <h2>{title}</h2>
        {mode === "create" && dependencies.length > 0 && (
          <div className="admin-crud-deps" data-testid={`${testId}-dependencies`}>
            <p className="kicker">DEPENDENCIES</p>
            <ul className="admin-list">
              {dependencies.map((dep) => (
                <li key={dep.key}>
                  <strong>{dep.label}</strong>
                  {dep.required ? " · required" : " · optional"}
                  {dep.help && <> — {dep.help}</>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {fields.map((field) => (
          <FieldControl
            key={field.key}
            field={field}
            form={form}
            setForm={setForm}
            readOnly={readOnly}
            lockKey={mode === "edit" && field.key === rowKey}
            dependencyOptions={dependencyOptions[field.key]}
            testId={testId}
          />
        ))}
        {needsApproval && !readOnly && (
          <p className="admin-note">Next step: review your changes and submit with approval rationale.</p>
        )}
        {error && <div className="error-message">{error}</div>}
        <div className="login-actions">
          <button type="button" className="secondary-button" onClick={onClose}>{readOnly ? "Close" : "Cancel"}</button>
          {!readOnly && (
            <button type="submit" className="primary-button" data-testid={`${testId}-save`}>
              {needsApproval ? "Continue to review" : mode === "create" ? "Create" : "Save changes"}
            </button>
          )}
        </div>
      </form>
    </ModalScrim>
  );
}

function FieldControl({ field, form, setForm, readOnly, lockKey, dependencyOptions, testId }) {
  const disabled = readOnly || lockKey;
  const rawOpts = field.options || dependencyOptions || [];
  const opts = rawOpts.map((o) => (typeof o === "string" ? { value: o, label: o } : { value: o.value, label: o.label || o.value }));

  return (
    <label key={field.key}>
      {field.label}
      {field.type === "textarea" ? (
        <textarea
          rows={3}
          readOnly={disabled}
          value={form[field.key] ?? ""}
          onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
          data-testid={`${testId}-field-${field.key}`}
        />
      ) : field.type === "select" || (dependencyOptions && dependencyOptions.length > 0) ? (
        <select
          disabled={readOnly}
          value={form[field.key] ?? ""}
          onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
          data-testid={`${testId}-field-${field.key}`}
        >
          <option value="">— select —</option>
          {opts.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      ) : field.type === "checkbox" ? (
        <input
          type="checkbox"
          disabled={readOnly}
          checked={!!form[field.key]}
          onChange={(e) => setForm({ ...form, [field.key]: e.target.checked })}
          data-testid={`${testId}-field-${field.key}`}
        />
      ) : (
        <input
          type={field.type || "text"}
          readOnly={disabled}
          required={!readOnly && field.required}
          placeholder={field.placeholder}
          value={form[field.key] ?? ""}
          onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
          data-testid={`${testId}-field-${field.key}`}
        />
      )}
    </label>
  );
}

function ReviewApprovalModal({ mode, entityLabel, payload, fields, onClose, onConfirm, testId }) {
  const [rationale, setRationale] = useState("");
  const [err, setErr] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!(rationale || "").trim()) {
      setErr("Approval rationale is required.");
      return;
    }
    onConfirm(rationale.trim());
  };
  return (
    <ModalScrim testId={`${testId}-review-modal`}>
      <form className="modal wide" onSubmit={submit}>
        <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        <p className="kicker">REVIEW & APPROVAL · {entityLabel.toUpperCase()}</p>
        <h2>{mode === "create" ? "Approve new record" : "Approve changes"}</h2>
        <p className="muted small-note">Review the proposed values before they take effect. This simulates governed control-plane change approval.</p>
        <dl className="admin-review-dl">
          {fields.filter((f) => payload[f.key] != null && String(payload[f.key]).trim() !== "").map((f) => (
            <div key={f.key} className="admin-review-row">
              <dt>{f.label}</dt>
              <dd>{String(payload[f.key])}</dd>
            </div>
          ))}
        </dl>
        <label>
          Approval rationale
          <textarea
            rows={3}
            required
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder="Why is this change authorized?"
            data-testid={`${testId}-approval-rationale`}
          />
        </label>
        {err && <div className="error-message">{err}</div>}
        <div className="login-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Back</button>
          <button type="submit" className="primary-button" data-testid={`${testId}-approval-submit`}>Submit with approval</button>
        </div>
      </form>
    </ModalScrim>
  );
}

function DeleteApprovalModal({ entityLabel, row, rowKey, needsApproval, onClose, onConfirm, testId }) {
  const [rationale, setRationale] = useState("");
  const [err, setErr] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (needsApproval && !(rationale || "").trim()) {
      setErr("Approval rationale is required to delete.");
      return;
    }
    onConfirm(rationale.trim());
  };
  return (
    <ModalScrim testId={`${testId}-delete-scrim`}>
      <form className="modal" onSubmit={submit}>
        <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        <p className="kicker">DELETE · {entityLabel.toUpperCase()}</p>
        <h2>Remove {row[rowKey]}?</h2>
        <p className="muted">
          {needsApproval
            ? "Deletion requires review and approval rationale. Catalog seed rows may reappear on full reload until removed server-side."
            : "This removes the row from the console listing."}
        </p>
        {needsApproval && (
          <label>
            Approval rationale
            <textarea
              rows={3}
              required
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              data-testid={`${testId}-delete-rationale`}
            />
          </label>
        )}
        {err && <div className="error-message">{err}</div>}
        <div className="login-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button type="submit" className="primary-button danger-button" data-testid={`${testId}-confirm-delete`}>
            {needsApproval ? "Approve deletion" : "Delete"}
          </button>
        </div>
      </form>
    </ModalScrim>
  );
}
